import * as gateway from 'sfx-web-contract';
import { AlkLogger } from '@alk/core';
import { COMMUNICATION_SERVICE, ICommunicationService } from '../../communication';
import { decodeForwardQuoteResponse, encodeForwardQuoteRequest } from './forward-stream-adapter.utils';
import { filter, finalize, map, takeUntil, tap } from 'rxjs/operators';
import { ForwardPriceRequestWithClient, ISessionDisposable, IStreamData } from '../../../interfaces';
import { IForwardQuote } from '../../../interfaces/forward-quote.interface';
import { IForwardStreamAdapterService } from './forward-stream-adapter.interface';
import { inject, injectable } from 'inversify';
import { Observable, Subject } from 'rxjs';
import { terminate } from '../../../helpers';

@injectable()
export class ForwardStreamAdapterService implements IForwardStreamAdapterService, ISessionDisposable {

    @inject(COMMUNICATION_SERVICE) private readonly communicationService: ICommunicationService;

    private _rawSpotFeed: Observable<IStreamData<Uint8Array>>;

    private readonly destroy$: Subject<boolean> = new Subject<boolean>();

    priceObservable = (request: ForwardPriceRequestWithClient): Observable<IForwardQuote> => {

        const token =  this.createSubscriptionInternal(request);

        AlkLogger.debug(`[FORWARD_STREAM_ADAPTER#priceObservable] for request ${ JSON.stringify(request) } ${ ''
        } created subscription with id ${ token } `);

        return this.rawSpotFeed().pipe(
            filter(data => data.communicationToken === token),
            terminate(quote => quote.terminal),
            map(quote => {
                if (Boolean(quote.error)) {
                    AlkLogger.error(`[FORWARD_STREAM_ADAPTER#priceObservable] stream request ${ JSON.stringify(request) } ${ ''
                    } for subscription id ${ token } ends with error: ${ JSON.stringify(quote.error) }`);
                    throw quote.error;
                }
                return quote;
            }),
            map(decodeForwardQuoteResponse(request.productType)),
            tap(quote =>
                AlkLogger.debug(`[FORWARD_STREAM_ADAPTER#priceObservable] decoded quote ${ JSON.stringify(quote) } ${ ''
                } for subscription id ${ token } `)),
            finalize(this.terminateFeed(token)),
        );
    };

    private rawSpotFeed(): Observable<IStreamData<Uint8Array>> {
        if (!Boolean(this._rawSpotFeed)){
            this._rawSpotFeed = this.communicationService
                .dataObservable(gateway.MessageTypes.ForwardQuote)
                .pipe(
                    takeUntil(this.destroy$),
                );
        }

        return this._rawSpotFeed;
    }

    stop(): void {
        this.destroy$.next(true);
        this._rawSpotFeed = null;
    }

    private readonly terminateFeed = (token) => () => {

        AlkLogger.debug(`[FORWARD_STREAM_ADAPTER#priceObservable] feed complete for ${ token }`);

        if (this.communicationService.subscriptionsList().indexOf(token) >= 0){
            this.communicationService.delete(token);
        }
    };

    private createSubscriptionInternal(request: ForwardPriceRequestWithClient): number{

        const rawRequest = encodeForwardQuoteRequest(request);

        const requestId = this.communicationService.create(rawRequest, gateway.MessageTypes.ForwardQuote);

        AlkLogger.debug(`[ForwardStreamAdapterService#priceObservable] for request ${ JSON.stringify(request) } ${ ''
        }created subscription with token ${ requestId } `);

        return requestId;
    }
}
